import React, { FunctionComponent, Suspense, useEffect, useMemo, useRef } from "react";
import styled from "styled-components";
import { useCookies } from "react-cookie";
import { useDispatch, useSelector } from "react-redux";

import { useLocation, useNavigate } from "react-router-dom";

import SEO from "./SEO";

import Footer from "components/Footer";
import ScrollToTop from "components/ScrollToTop/ScrollToTop";
import Header from "containers/Header";
import AppRoutes from "routes/routes-mapping";
import Modal from "containers/Modal";
import useOffersRefreshing from "hooks/useOffersRefreshing";
import useQueryParamSaver from "hooks/useQueryParamSaver";
import useTracking from "hooks/useTracking";
import { configState, fetchConfigFromAPI } from "state/config-slice";
import { activeOffers } from "state/offers-slice";
import { AppDispatch } from "state/reducers-combiner";
import { userState, fetchUserFromAPI } from "state/user-slice";
import useAxiosInterceptor from "connection/useAxiosInterceptor";
import useMedalliaInterceptor from "connection/useMedalliaInterceptor";

import useDeeplink from "hooks/useDeeplink";
import useQueryCallbackHandler from "hooks/useQueryCallbackHandler";
import useCookieCart from "hooks/useCookieCart";
import useRemoveCookies from "hooks/useRemoveCookies";
import getMedalliaCountryMapping from "utils/getMedalliaCountryMapping";
import getOpco from "utils/getOpco";
import { getPartnerImageUrl } from "utils/getImageUrl";
import { initDebug } from "utils/debugHelper";
import { useExternalScriptWithData, UtagData } from "hooks/useExternalScript";
import { useSurveyConsentHandler } from "hooks/useSurveyConsentHandler";
import { MedalliaData } from "types/MedalliaData";

const Loader = React.lazy(() => import(/* webpackChunkName: "Loader", webpackPrefetch: true */ "components/Loader"));
const IframeLayer = React.lazy(
  () => import(/* webpackChunkName: "IframeLayer", webpackPrefetch: true */ "containers/IframeLayer"),
);

const ContentWrapper = styled.div<{
  compensateFixedHeader: boolean;
}>`
  flex-grow: 1;
  position: relative;
  ${({ theme }) => theme.customTheme.breakpoints.up("md")} {
    ${({ compensateFixedHeader }) =>
      compensateFixedHeader &&
      `
      margin-top: 63px;
    `}
  }

  ${({ compensateFixedHeader }) =>
    compensateFixedHeader &&
    `
    margin-top: 76px;
  `}
`;

export const LoaderPlaceholder = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  background-color: white;
  right: 0;
  left: 0;
  z-index: 1000;
`;

const emptySurvey: MedalliaData = {
  config: {
    enabledForMarket: false,
    sdkUrl: "",
    forms: [],
  },
  history: [],
};

const App: FunctionComponent = () => {
  const dispatch: AppDispatch = useDispatch();
  const user = useSelector(userState);
  const userActiveOffers = useSelector(activeOffers);
  const { cdnUrl, survey, surveyLoading, loaded, isMenuOpen, requiresLandingPage } = useSelector(configState);
  const [cookies] = useCookies();
  const OpcolessCdnUrl = cdnUrl.replace(`/${getOpco()}/`, "/");
  const opco = getOpco();
  const { loadScript, loadMedalliaForm, isSdkLoaded, removeScriptAndCookies } = useExternalScriptWithData();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const hasUserFetchBeenCalled = useRef(false);

  const consentUnknown = cookies?.SUBSCRIPTIONPLUS_TRACKING === undefined;
  const consentGranted = cookies?.SUBSCRIPTIONPLUS_TRACKING === true || cookies?.SUBSCRIPTIONPLUS_TRACKING === "true";
  const hasActiveSubscription = userActiveOffers.length > 0;

  const utagData: UtagData = useMemo(() => {
    return {
      page_country: opco.toUpperCase(),
      page_language: getMedalliaCountryMapping(opco),
      tealium_environment: survey?.config?.tealimumEnv || "dev",
      page_url: window.location.href,
      hasSubscription: hasActiveSubscription,
    };
  }, [hasActiveSubscription, opco, survey?.config?.tealimumEnv]);

  useAxiosInterceptor();
  useMedalliaInterceptor();
  useQueryParamSaver();
  useTracking();
  useOffersRefreshing(cdnUrl || "");
  useRemoveCookies();

  useEffect(() => {
    dispatch(fetchConfigFromAPI());
  }, [dispatch]);

  useEffect(() => {
    // Check if we need to redirect to /cable after user data is loaded
    const normalizedPathname = pathname.replace(/\/+$/, "");
    const isCookiePreferencesPath = normalizedPathname.endsWith("/cookie-preferences");

    if (
      user.loaded &&
      user.loggedIn &&
      user.mustSelectContract &&
      user.account?.accountContracts?.length >= 2 &&
      !isCookiePreferencesPath
    ) {
      navigate("/cable");
    }
  }, [user.loaded, user.loggedIn, user.mustSelectContract, user.account?.accountContracts?.length, navigate, pathname]);

  useEffect(() => {
    if (loaded && !hasUserFetchBeenCalled.current) {
      if (requiresLandingPage) {
        navigate("/landingPage");
      } else {
        hasUserFetchBeenCalled.current = true;
        dispatch(fetchUserFromAPI()).catch(() => {
          // Error already handled by the thunk and axios interceptor
        });
      }
    }
  }, [dispatch, loaded, navigate, requiresLandingPage]);

  // Handle failed authentication (e.g., 401 error from API)
  useEffect(() => {
    if (hasUserFetchBeenCalled.current && user.loaded && !user.loggedIn && requiresLandingPage) {
      navigate("/landingPage");
    }
  }, [user.loaded, user.loggedIn, navigate, requiresLandingPage]);

  useDeeplink();

  useQueryCallbackHandler();

  useCookieCart();

  useSurveyConsentHandler({
    survey: survey || emptySurvey,
    isSdkLoaded,
    user,
    activeOffers: userActiveOffers,
    consentUnknown,
    consentGranted,
    surveyLoading: Boolean(surveyLoading),
    loadMedalliaForm,
    removeScriptAndCookies,
    loadScript,
    utagData,
  });

  useEffect(() => {
    initDebug();

    // Favicon update logic
    const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
    const faviconUrl =
      opco === "lw"
        ? `${getPartnerImageUrl("generic/lowi.ico", OpcolessCdnUrl)}`
        : `${getPartnerImageUrl("generic/favicon.ico", OpcolessCdnUrl)}`;

    if (link) {
      link.href = faviconUrl;
    } else {
      const newLink = document.createElement("link");
      newLink.rel = "icon";
      newLink.href = faviconUrl;
      document.head.appendChild(newLink);
    }
  }, [opco, OpcolessCdnUrl]);

  return (
    <>
      <Suspense fallback={<LoaderPlaceholder />}>
        <SEO />
        <ScrollToTop />
        <Modal />
        <Loader />
        <IframeLayer />
      </Suspense>
      {user.loaded && (
        <>
          <Header />
          <ContentWrapper compensateFixedHeader={isMenuOpen}>
            <Suspense fallback={null}>
              <AppRoutes />
            </Suspense>
          </ContentWrapper>
          <Footer />
        </>
      )}
    </>
  );
};

export default App;
